import React, { useState } from "react";
import axios from "axios";

const PricingTypesList = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [editedData, setEditedData] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isAddingNew, setIsAddingNew] = useState(false);
  const [pricingData, setPricingData] = useState([
    {
      id: 1,
      name: "Pay Per User",
      type: "Usage-based",
      status: "Active",
      description: "Pay only for number of added users",
    },
    {
      id: 2,
      name: "Pay Per API Call",
      type: "Usage-based",
      status: "Active",
      description: "Pay only for number of API calls",
    },
    {
      id: 3,
      name: "Enterprise",
      type: "Subscription",
      status: "Active",
      description: "Yearly subscription with unlimited usage",
    },
  ]);

  const emptyPricing = {
    id: "new",
    name: "",
    type: "Usage-based",
    status: "Active",
    description: "",
  };

  const handleAddNew = () => {
    setIsAddingNew(true);
    setEditingId("new");
    setEditedData(emptyPricing);
    setError(null);
    setPricingData([emptyPricing, ...pricingData]);
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      setError(null);

      let response;
      if (editingId === "new") {
        response = await axios.post("/api/pricing-types", editedData);
        if (response.status === 201) {
          const updatedData = pricingData.filter(
            (pricing) => pricing.id !== "new"
          );
          setPricingData([response.data, ...updatedData]);
          setIsAddingNew(false);
        }
      } else {
        response = await axios.put(
          `/api/pricing-types/${editedData.id}`,
          editedData
        );
        if (response.status === 200) {
          const updatedData = pricingData.map((item) =>
            item.id === editedData.id ? response.data : item
          );
          setPricingData(updatedData);
        }
      }
      setEditingId(null);
      setEditedData({});
    } catch (err) {
      setError(err.response?.data?.message || "Error saving changes");
      console.error("Error saving pricing type:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    if (isAddingNew) {
      setPricingData(pricingData.filter((pricing) => pricing.id !== "new"));
      setIsAddingNew(false);
    }
    setEditingId(null);
    setEditedData({});
    setError(null);
  };

  const handleEdit = (item) => {
    setEditingId(item.id);
    setEditedData(item);
    setError(null);
  };

  const handleDelete = async (item) => {
    if (window.confirm("Are you sure you want to delete this pricing type?")) {
      try {
        setLoading(true);
        setError(null);

        const response = await axios.delete(`/api/pricing-types/${item.id}`);
        if (response.status === 200) {
          setPricingData(
            pricingData.filter((pricing) => pricing.id !== item.id)
          );
        }
      } catch (err) {
        setError(err.response?.data?.message || "Error deleting pricing type");
        console.error("Error deleting pricing type:", err);
      } finally {
        setLoading(false);
      }
    }
  };

  const handleChange = (field, value) => {
    setEditedData({
      ...editedData,
      [field]: value,
    });
    setError(null);
  };

  const filteredData = pricingData.filter(
    (item) =>
      item.id === "new" ||
      item.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="container mt-4">
      {error && (
        <div className="alert alert-danger mb-4" role="alert">
          {error}
        </div>
      )}

      <div className="listing-header d-flex justify-content-between align-items-center">
        <h2 className="listing-title">Pricing Types</h2>
        <button
          className="btn-add-new"
          onClick={handleAddNew}
          disabled={loading || isAddingNew}
        >
          <i className="fas fa-plus-circle"></i>
          <span>Add New Pricing</span>
        </button>
      </div>

      <div className="mb-4">
        <input
          type="text"
          className="form-control listing-search"
          placeholder="Search Pricing Types..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>

      <div className="listing-table">
        <div className="table-responsive">
          <table className="table table-hover mb-0">
            <thead>
              <tr>
                <th className="text-start">Name</th>
                <th className="text-start">Type</th>
                <th className="text-start">Status</th>
                <th className="text-start">Description</th>
                <th className="text-end">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredData.map((item) => (
                <tr
                  key={item.id}
                  className={item.id === "new" ? "new-item-row" : ""}
                >
                  <td>
                    {editingId === item.id ? (
                      <input
                        type="text"
                        className="form-control form-control-sm"
                        value={editedData.name}
                        onChange={(e) => handleChange("name", e.target.value)}
                        placeholder="Enter pricing name"
                        autoFocus
                      />
                    ) : (
                      item.name
                    )}
                  </td>
                  <td>
                    {editingId === item.id ? (
                      <select
                        className="form-select form-select-sm"
                        value={editedData.type}
                        onChange={(e) => handleChange("type", e.target.value)}
                      >
                        <option value="Usage-based">Usage-based</option>
                        <option value="Subscription">Subscription</option>
                        <option value="One-time">One-time</option>
                        <option value="Tiered">Tiered</option>
                      </select>
                    ) : (
                      item.type
                    )}
                  </td>
                  <td>
                    {editingId === item.id ? (
                      <select
                        className="form-select form-select-sm"
                        value={editedData.status}
                        onChange={(e) => handleChange("status", e.target.value)}
                      >
                        <option value="Active">Active</option>
                        <option value="Inactive">Inactive</option>
                        <option value="Beta">Beta</option>
                      </select>
                    ) : (
                      <span
                        className={`badge bg-${
                          item.status === "Active"
                            ? "success"
                            : item.status === "Inactive"
                            ? "danger"
                            : "warning"
                        }`}
                      >
                        {item.status}
                      </span>
                    )}
                  </td>
                  <td>
                    {editingId === item.id ? (
                      <input
                        type="text"
                        className="form-control form-control-sm"
                        value={editedData.description}
                        onChange={(e) =>
                          handleChange("description", e.target.value)
                        }
                        placeholder="Enter description"
                      />
                    ) : (
                      item.description
                    )}
                  </td>
                  <td>
                    <div className="action-buttons text-end">
                      {editingId === item.id ? (
                        <>
                          <button
                            className="btn btn-outline-success btn-icon"
                            onClick={handleSave}
                            disabled={loading}
                          >
                            {loading ? (
                              <i className="fas fa-spinner fa-spin"></i>
                            ) : (
                              <i className="fas fa-check"></i>
                            )}
                          </button>
                          <button
                            className="btn btn-outline-secondary btn-icon ms-2"
                            onClick={handleCancel}
                            disabled={loading}
                          >
                            <i className="fas fa-times"></i>
                          </button>
                        </>
                      ) : (
                        <>
                          <button
                            className="btn btn-outline-primary btn-icon"
                            onClick={() => handleEdit(item)}
                            disabled={loading}
                          >
                            <i className="fas fa-edit"></i>
                          </button>
                          <button
                            className="btn btn-outline-danger btn-icon ms-2"
                            onClick={() => handleDelete(item)}
                            disabled={loading}
                          >
                            {loading ? (
                              <i className="fas fa-spinner fa-spin"></i>
                            ) : (
                              <i className="fas fa-trash"></i>
                            )}
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default PricingTypesList;
